import { useCallback } from "react";
import { useDataProvider, useGetIdentity } from "ra-core";

import {
  cleanupClientForSave,
  validateChildrenCount,
  validateClientBirthday,
  validateClientCurp,
  validateClientMaritalRegime,
  validateClientRfc,
} from "../clients/clientValidation";
import {
  CLIENT_TYPES,
  MARITAL_PROPERTY_REGIMES,
  MARITAL_STATUSES,
} from "../types";
import { createEachRow } from "./createEachRow";
import { toInteger, toIsoDate, toText } from "./parseCell";
import type { ImportCell, ProcessImportBatch } from "./types";

/**
 * Creates a client per CSV row, mirroring `useCompanyImport`. Unlike a
 * company, a client row is validated with the same TASK-022 rules the
 * create/edit form enforces (client_type, RFC length by client_type, CURP
 * shape, birthday, children_count) before it is ever sent to the data
 * provider: an invalid row is rejected by its position in the batch, with a
 * generic reason that never echoes the offending cell (AML client-
 * identification rule, MEMORY.md — a client field must never reach a log).
 *
 * ASSUMPTION: imported clients are owned by the importing user, same as every
 * other importer (`sales_id: identity?.id`); the clients RLS `WITH CHECK`
 * (TASK-017) enforces that server-side too.
 */
export function useClientImport(): ProcessImportBatch {
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();

  return useCallback(
    (batch) =>
      createEachRow(
        batch.map((row, index) => {
          // Position within this batch — the parser (`usePapaParse`) never
          // hands the importer a global row index, so this number resets at
          // every batch boundary, same limitation every importer here has.
          const rowNumber = index + 1;
          const reject = (reason: string) =>
            Promise.reject(
              new Error(`Client import row ${rowNumber}: ${reason}`),
            );

          const fullLegalName = toText(row.full_legal_name);
          if (!fullLegalName) {
            return reject("missing full_legal_name");
          }

          const clientType = toEnumValue(row.client_type, CLIENT_TYPES);
          if (!clientType) {
            return reject("invalid client_type");
          }

          const rfc = toText(row.rfc)?.toUpperCase();
          if (validateClientRfc(rfc, { client_type: clientType })) {
            return reject("invalid rfc");
          }

          const curp = toText(row.curp)?.toUpperCase();
          if (validateClientCurp(curp, { client_type: clientType })) {
            return reject("invalid curp");
          }

          const birthday = toIsoDate(row.birthday)?.slice(0, 10);
          if (validateClientBirthday(birthday)) {
            return reject("invalid birthday");
          }

          const childrenCount = toInteger(row.children_count);
          if (validateChildrenCount(childrenCount)) {
            return reject("invalid children_count");
          }

          const maritalStatus = toEnumValue(
            row.marital_status,
            MARITAL_STATUSES,
          );
          const maritalPropertyRegime = toEnumValue(
            row.marital_property_regime,
            MARITAL_PROPERTY_REGIMES,
          );
          if (
            validateClientMaritalRegime(maritalPropertyRegime, {
              marital_status: maritalStatus,
            })
          ) {
            return reject("invalid marital_property_regime");
          }

          const data = cleanupClientForSave({
            client_type: clientType,
            full_legal_name: fullLegalName,
            rfc,
            curp,
            birthday,
            marital_status: maritalStatus,
            marital_property_regime: maritalPropertyRegime,
            children_count: childrenCount,
            email: toText(row.email),
            phone: toText(row.phone),
            address: toText(row.address),
            sales_id: identity?.id,
          });

          return dataProvider.create("clients", {
            data: { ...data, created_at: new Date().toISOString() },
          });
        }),
      ),
    [dataProvider, identity?.id],
  );
}

/** Matches a cell against a fixed enum's values, case-insensitively. */
const toEnumValue = <T extends string>(
  cell: ImportCell,
  values: readonly T[],
): T | undefined => {
  const text = toText(cell)?.toLowerCase();
  return values.find((value) => value.toLowerCase() === text);
};
